"use client";

import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  ACCENT_TEXT,
  DARK,
  DISPLAY,
  EDITORIAL_SECTION_CLS,
  GROUND,
  INK,
  mix,
  SECTION_INTRO_CLS,
  SECTION_TITLE_CLS,
} from "./theme";
import { BgNumeral, Grain, SectionMarker } from "./primitives";

export type FaqItem = { q: string; a: string };

/**
 * Editorial FAQ — section marker + title/intro, then a single-open
 * accordion of hairline-separated questions. Claret accent on the open
 * row, in the house style. Client component (open state).
 */
export function FaqSection({
  section,
  label,
  numeral,
  title,
  intro,
  items,
  contained = false,
}: {
  section?: string;
  label?: string;
  numeral?: string;
  title: string;
  intro?: string;
  items: FaqItem[];
  /** Use the parent page's rail instead of adding another set of gutters. */
  contained?: boolean;
}) {
  const [open, setOpen] = useState<number | null>(0);
  const disclosureId = useId();

  return (
    <section
      id="faq"
      className={`relative overflow-hidden ${contained ? "scroll-mt-24 border-b border-dotted border-[var(--rule)]" : "scroll-mt-8"}`}
      style={{ backgroundColor: GROUND, color: INK }}
    >
      {numeral ? <BgNumeral numeral={numeral} /> : null}
      <Grain opacity={0.45} />
      <div className={`relative z-10 ${contained ? "py-12" : EDITORIAL_SECTION_CLS}`}>
        {section && label ? <SectionMarker section={section} label={label} /> : null}

        <div className={`${section && label ? "mt-10" : ""} max-w-3xl`}>
          <h2
            style={{ ...DISPLAY, color: INK }}
            className={SECTION_TITLE_CLS}
          >
            {title}
          </h2>
          {intro && (
            <p
              className={`mt-6 ${SECTION_INTRO_CLS}`}
              style={{ color: mix("--theme-ink", 88) }}
            >
              {intro}
            </p>
          )}
        </div>

        <ul className="mt-12 max-w-4xl md:mt-14">
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <li
                key={item.q}
                className="border-t last:border-b"
                style={{ borderColor: mix("--theme-ink", 16) }}
              >
                <button
                  id={`${disclosureId}-button-${i}`}
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`${disclosureId}-panel-${i}`}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-4 py-5 text-left transition-colors motion-reduce:transition-none"
                >
                  <span
                    style={{ ...DISPLAY, color: isOpen ? ACCENT_TEXT : INK }}
                    className="text-[1.1rem] font-semibold leading-[1.3] tracking-[-0.01em] md:text-[1.25rem]"
                  >
                    {item.q}
                  </span>
                  <ChevronDown
                    aria-hidden="true"
                    className="h-5 w-5 shrink-0 transition-transform duration-200 motion-reduce:transition-none"
                    style={{
                      color: isOpen ? DARK : mix("--theme-ink", 45),
                      transform: isOpen ? "rotate(180deg)" : "none",
                    }}
                  />
                </button>
                <section
                  id={`${disclosureId}-panel-${i}`}
                  aria-labelledby={`${disclosureId}-button-${i}`}
                  aria-hidden={!isOpen}
                  className="grid transition-all duration-200 ease-out motion-reduce:transition-none"
                  style={{
                    gridTemplateRows: isOpen ? "1fr" : "0fr",
                    opacity: isOpen ? 1 : 0,
                    visibility: isOpen ? "visible" : "hidden",
                  }}
                >
                  <div className="overflow-hidden">
                    <div
                      className="max-w-[68ch] space-y-4 pb-6 text-[1rem] leading-[1.65]"
                      style={{ color: mix("--theme-ink", 88) }}
                    >
                      {item.a.split("\n\n").map(paragraph => <p key={paragraph}>{paragraph}</p>)}
                    </div>
                  </div>
                </section>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
